import { useEffect, useState } from 'react';
import { io } from 'socket.io-client';
import { useParams } from '@tanstack/react-router';
import { DriverCard, FloatPill, Handle, MapSheet } from '../components/ui';
import { Logo } from '../components/Logo';
import { MapView } from '../components/MapView';
import { Screen } from '../components/Screen';
import { API_BASE } from '../lib/api';
import { clock, km } from '../lib/format';
import { roadEstimate } from '../lib/geo';
import { shortName } from '../lib/places';
import type { LatLng, Place, VehicleType } from '../lib/types';
import { words } from '../lib/vehicles';
import { useT } from '../lib/i18n';
import { mapCut, useLayout } from '../lib/layout';
import { EmptyArt } from '../art/Empty';
import type { StringKey } from '../lib/strings';
import { DropSquare, MapStrip, StartRing, VehicleDisc } from './mapBits';

type ShareView = {
  status: string;
  ended: boolean;
  riderFirstName: string | null;
  vehicleType: VehicleType;
  pickup: Place;
  drop: Place;
  driver: { firstName: string | null; vehicle: { make: string; model: string; colour: string; plateDisplay: string } | null; location: (LatLng & { at: string }) | null } | null;
  startedAt: string | null;
};

/**
 * Public live-trip page for a share link (no sign-in): what a trusted contact opens. Live over the socket
 * (`auth: { shareToken }` → `share:updated`, `ride:driver-location`), with `GET /v1/share/:token` on open and every
 * 5 s while the socket is down. 410 → the trip ended (or the link was stopped); 404 → not a link.
 */
export function Track() {
  const { token } = useParams({ from: '/t/$token' });
  const t = useT();
  const [v, setV] = useState<ShareView | null>(null);
  const [gone, setGone] = useState<'ended' | 'missing' | null>(null);
  const [connected, setConnected] = useState(false);
  useEffect(() => {
    let live = true;
    const load = async () => {
      try {
        const res = await fetch(`${API_BASE}/v1/share/${encodeURIComponent(token)}`);
        if (!res.ok) return live && setGone(res.status === 404 ? 'missing' : 'ended');
        const data = (await res.json()) as ShareView;
        if (live) setV(data);
      } catch {
        /* retry next tick */
      }
    };
    void load();
    const socket = io(API_BASE || window.location.origin, { auth: { shareToken: token }, transports: ['websocket'] });
    socket.on('ready', () => (setConnected(true), void load()));
    socket.on('disconnect', () => setConnected(false));
    socket.on('session:error', ({ code }: { code: string }) => {
      if (code === 'SHARE_LINK_EXPIRED') setGone('ended');
      socket.close();
    });
    socket.on('share:updated', (view: ShareView) => live && setV(view));
    socket.on('ride:driver-location', (p: LatLng & { at: string }) =>
      setV((cur) => (cur?.driver ? { ...cur, driver: { ...cur.driver, location: { lat: p.lat, lng: p.lng, at: p.at } } } : cur)),
    );
    const tm = window.setInterval(() => !socket.connected && void load(), 5000);
    return () => {
      live = false;
      socket.close();
      window.clearInterval(tm);
    };
  }, [token]);

  const who = v?.riderFirstName ?? t('track.friend');
  const loc = v?.driver?.location ?? null;
  const toDrop = loc && v ? roadEstimate(loc, v.drop) : null;
  const [sheetH, setSheetH] = useState(300);
  const L = useLayout();
  return (
    <Screen name="on-the-trip" label={t('track.label')}>
      {v && (
        <MapView label={t('track.map')} center={v.pickup} zoom={13} style={{ left: 0, right: 0, top: 0, bottom: mapCut(L, sheetH, 30) }} fit={{ points: [v.pickup, v.drop, ...(loc ? [loc] : [])], padding: { top: 120, bottom: 70, left: 60, right: 60 } }}>
          <StartRing at={v.pickup} />
          <DropSquare at={v.drop} />
          {loc && <VehicleDisc at={loc} type={v.vehicleType} halo />}
        </MapView>
      )}
      {!L.wide || L.compact ? (
        <FloatPill style={{ position: 'absolute', left: 'calc(var(--map-l) + 20px)', top: 'calc(var(--sat) + 14px)', zIndex: 5 }}>
          <Logo size={22} /> Zoomo Rides
        </FloatPill>
      ) : null}
      <MapSheet label={t('track.label')} onHeight={setSheetH} art={<MapStrip />}>
        <Handle />
        <div key={gone || v?.ended ? 'end' : v ? v.status : 'load'} className="zr-swap" style={{ paddingBottom: 8 }}>
        {gone || v?.ended ? (
          <>
            <EmptyArt kind="ended" width={120} style={{ marginTop: 18 }} />
            <h1 style={{ margin: '14px 0 0', fontSize: 24, fontWeight: 700 }}>{gone === 'missing' ? t('track.notFound') : t('track.ended')}</h1>
            <p style={{ margin: '6px 0 0', fontSize: 15, fontWeight: 500, color: 'var(--ink2)' }}>{gone === 'missing' ? t('track.notFoundBody') : t('track.endedBody')}</p>
          </>
        ) : !v ? (
          <p style={{ margin: '20px 0 0', fontSize: 15, color: 'var(--ink2)' }}>{t('common.loading')}</p>
        ) : (
          <>
            <h1 style={{ margin: '20px 0 0', fontSize: 24, lineHeight: '30px', fontWeight: 700, letterSpacing: '-0.02em' }}>
              <span data-testid="track-status" data-status={v.status} data-live={connected ? '1' : '0'}>
                {who} · {t(`track.${v.status}` as StringKey)}
              </span>
            </h1>
            <p style={{ margin: '6px 0 0', fontSize: 15, lineHeight: '22px', fontWeight: 500, color: 'var(--ink2)' }}>
              {shortName(v.pickup.address)} → {shortName(v.drop.address)}
              {v.status === 'STARTED' && toDrop ? t('track.left', { mins: Math.max(1, Math.round(toDrop.seconds / 60)), left: km(toDrop.meters), time: clock(new Date(Date.now() + toDrop.seconds * 1000)) }) : ''}
            </p>
            {v.driver && (
              <DriverCard
                style={{ marginTop: 16, borderRadius: 18 }}
                size={44}
                name={v.driver.firstName ?? t('track.driver')}
                line={v.driver.vehicle ? `${v.driver.vehicle.make} ${v.driver.vehicle.model} · ${v.driver.vehicle.colour.toLowerCase()}` : words(t, v.vehicleType).label}
                plate={v.driver.vehicle?.plateDisplay}
              />
            )}
            <p style={{ margin: '14px 0 0', fontSize: 13, fontWeight: 500, color: 'var(--ink3)' }}>{t('track.worried')}</p>
          </>
        )}
        </div>
      </MapSheet>
    </Screen>
  );
}
